import { useEffect, useMemo, useState } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import { ChevronDown } from 'lucide-react'
import { useAuth } from '@/contexts/AuthContext'
import { fetchUnreadCounts } from '@/lib/api'
import { cn } from '@/lib/utils'
import { ORG } from '@/lib/org'
import { ROLE_LABEL } from '@/lib/types'
import { GROUPS, TOP_ITEMS, canSeePage } from '@/lib/navigation'
import { usePageAccess } from '@/contexts/PageAccessContext'
import { isTmsStaff, useTmsAccess } from '@/lib/tms'

/** Chu kỳ làm mới badge tin nhắn chưa đọc trên menu Trao đổi. */
const UNREAD_REFRESH_MS = 60_000

const OPEN_GROUPS_KEY = 'v75-sidebar-open-groups'

function readOpenGroups(): Record<string, boolean> {
  try {
    const raw = localStorage.getItem(OPEN_GROUPS_KEY)
    if (raw) return JSON.parse(raw) as Record<string, boolean>
  } catch { /* hỏng dữ liệu — dùng mặc định */ }
  return {}
}

interface SidebarProps {
  open?: boolean
  onClose?: () => void
}

export function Sidebar({ open = false, onClose }: SidebarProps) {
  const { profile } = useAuth()
  const role = profile?.user.role ?? 'user'
  const location = useLocation()
  const [chatUnread, setChatUnread] = useState(0)
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(readOpenGroups)

  const { access, ready } = usePageAccess()
  const userId = profile?.user.id
  const deptCode = profile?.department?.code ?? null
  const tmsAccess = useTmsAccess(userId, role === 'admin_he_thong')
  const tmsStaff = isTmsStaff(tmsAccess)
  const topVisible = TOP_ITEMS.filter(m => canSeePage(m, role, userId, access, deptCode))
  // Chưa tải xong phân quyền trang → chưa hiện nhóm (tránh lộ trang đang khoá)
  const groupsVisible = useMemo(
    () => (ready ? GROUPS : [])
      .map(g => ({ ...g, items: g.items.filter(i => (!i.tms || tmsStaff) && canSeePage(i, role, userId, access, deptCode)) }))
      .filter(g => g.items.length > 0),
    [role, userId, access, ready, deptCode, tmsStaff],
  )

  /** Nhóm chứa route đang mở — luôn được bung để người dùng không "mất" mình. */
  const activeGroupKey = useMemo(() => {
    for (const g of groupsVisible) {
      if (g.items.some(i => i.to === location.pathname)) return g.key
    }
    return null
  }, [groupsVisible, location.pathname])

  const isGroupOpen = (key: string) => key === activeGroupKey || (openGroups[key] ?? false)

  const toggleGroup = (key: string) => {
    setOpenGroups(prev => {
      // Nhóm đang chứa route active mà bấm đóng → ghi nhớ false vẫn bị
      // activeGroupKey mở lại; hành vi này là chủ ý (không giấu mục đang xem).
      const next = { ...prev, [key]: !isGroupOpen(key) }
      try { localStorage.setItem(OPEN_GROUPS_KEY, JSON.stringify(next)) } catch { /* bỏ qua */ }
      return next
    })
  }

  // Close sidebar on route change (mobile)
  useEffect(() => {
    onClose?.()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname])

  // Badge tin nhắn chưa đọc trên menu Trao đổi (Module 6 — v0.20)
  useEffect(() => {
    if (!profile) return
    let alive = true
    const load = () => {
      fetchUnreadCounts()
        .then(m => { if (alive) setChatUnread([...m.values()].reduce((s, n) => s + n, 0)) })
        .catch(() => { /* mạng lỗi — giữ số cũ */ })
    }
    load()
    const t = setInterval(load, UNREAD_REFRESH_MS)
    return () => { alive = false; clearInterval(t) }
  // Nạp lại khi đổi trang để badge giảm ngay sau khi đọc trong /chat
  }, [profile, location.pathname])

  const itemClass = (isActive: boolean) => cn(
    'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
    isActive
      ? 'bg-primary/10 text-primary'
      : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
  )

  return (
    <aside
      className={cn(
        'flex flex-col border-r bg-card w-64 flex-shrink-0',
        // Desktop: always visible
        'md:flex md:relative md:translate-x-0 md:z-auto',
        // Mobile: slide in/out overlay
        'fixed inset-y-0 left-0 z-30 transition-transform duration-200',
        open ? 'translate-x-0' : '-translate-x-full',
        'md:translate-x-0',
      )}
    >
      <div className="flex h-16 items-center gap-3 px-4 border-b">
        {/* Nền trắng cố định để logo hiển thị đúng cả ở chế độ tối */}
        <div className="h-10 w-10 flex-shrink-0 rounded-lg bg-white p-0.5 shadow-sm ring-1 ring-border">
          <img src={ORG.logoUrl} alt={`Logo ${ORG.shortName}`} className="h-full w-full object-contain" />
        </div>
        <div className="min-w-0">
          <div className="text-sm font-semibold leading-tight">Hệ thống Lương</div>
          <div className="text-[11px] text-muted-foreground leading-tight">{ORG.name}</div>
        </div>
      </div>

      <nav className="flex-1 overflow-y-auto p-3 space-y-1">
        {topVisible.map(({ to, label, icon: Icon }) => (
          <NavLink
            key={to}
            to={to}
            end={to === '/'}
            className={({ isActive }) => itemClass(isActive)}
          >
            <Icon className="h-4 w-4" />
            <span className="flex-1">{label}</span>
            {to === '/chat' && chatUnread > 0 && (
              <span className="rounded-full bg-destructive px-1.5 py-0.5 text-[11px] font-bold leading-none text-destructive-foreground">
                {chatUnread > 99 ? '99+' : chatUnread}
              </span>
            )}
          </NavLink>
        ))}

        {groupsVisible.map(g => {
          const opened = isGroupOpen(g.key)
          const GIcon = g.icon
          const hasActive = g.key === activeGroupKey
          return (
            <div key={g.key} className="pt-1">
              <button
                onClick={() => toggleGroup(g.key)}
                aria-expanded={opened}
                className={cn(
                  'flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm font-semibold transition-colors',
                  hasActive && !opened
                    ? 'text-primary hover:bg-accent'
                    : 'text-foreground/80 hover:bg-accent hover:text-accent-foreground',
                )}
              >
                <GIcon className="h-4 w-4" />
                <span className="flex-1 text-left">{g.label}</span>
                <ChevronDown className={cn('h-4 w-4 text-muted-foreground transition-transform', opened && 'rotate-180')} />
              </button>
              {opened && (
                <div className="mt-0.5 space-y-0.5 border-l ml-[1.15rem] pl-2">
                  {g.items.map(({ to, label, icon: Icon }) => (
                    <NavLink key={to} to={to} className={({ isActive }) => itemClass(isActive)}>
                      <Icon className="h-4 w-4" />
                      <span className="flex-1">{label}</span>
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          )
        })}
      </nav>

      <div className="border-t p-3 text-xs text-muted-foreground">
        Vai trò:
        <div className="mt-1 inline-block rounded bg-secondary px-2 py-0.5 text-secondary-foreground font-medium">
          {ROLE_LABEL[role]}
        </div>
      </div>
    </aside>
  )
}
